import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router';
import { lazy, Suspense, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { NotificationsProvider } from '@/contexts/NotificationsContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import PlanGate from '@/components/PlanGate';
import { FEATURE_REGISTRY } from '@gatheredhq/types';
import PublicRoute from '@/components/PublicRoute';
import AppLayout from '@/components/layout/AppLayout';
import { ChunkErrorBoundary } from '@/components/ChunkErrorBoundary';
import CookieBanner from '@/components/CookieBanner';

// Auth pages
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const CallbackPage = lazy(() => import('@/pages/auth/CallbackPage'));
const AcceptInvitePage = lazy(() => import('@/pages/auth/AcceptInvitePage'));

// Onboarding
const OnboardingPage = lazy(() => import('@/pages/onboarding/OnboardingPage'));

// Venue pages
const VenuesPage = lazy(() => import('@/pages/venues/VenuesPage'));
const VenueOverviewPage = lazy(() => import('@/pages/venues/VenueOverviewPage'));
const VenueMenuPage = lazy(() => import('@/pages/venues/VenueMenuPage'));
const VenueStaffPage = lazy(() => import('@/pages/venues/VenueStaffPage'));
const VenueSettingsPage = lazy(() => import('@/pages/venues/VenueSettingsPage'));
const IntegrationsPage = lazy(() => import('@/pages/venues/integrations/IntegrationsPage'));
const ZiinaIntegrationPage = lazy(() => import('@/pages/venues/integrations/ZiinaIntegrationPage'));
const ZiinaOAuthCallbackPage = lazy(() => import('@/pages/venues/integrations/ZiinaOAuthCallbackPage'));
const VenueQRPage = lazy(() => import('@/pages/venues/VenueQRPage'));
const VenueTranslationsPage = lazy(() => import('@/pages/venues/VenueTranslationsPage'));
const VenueAICreditsPage = lazy(() => import('@/pages/venues/VenueAICreditsPage'));

// Portals
const KitchenPortalPage = lazy(() => import('@/pages/venues/kitchen/KitchenPortalPage'));
const BarPortalPage = lazy(() => import('@/pages/venues/bar/BarPortalPage'));
const WaiterPortalPage = lazy(() => import('@/pages/venues/waiter/WaiterPortalPage'));
const DeliveryPortalPage = lazy(() => import('@/pages/venues/delivery/DeliveryPortalPage'));
const DeliveryHubPage = lazy(() => import('@/pages/venues/delivery-hub/DeliveryHubPage'));
const OrdersPage = lazy(() => import('@/pages/venues/orders/OrdersPage'));
const CashierPortalPage = lazy(() => import('@/pages/venues/cashier/CashierPortalPage'));
const FinancialsPage = lazy(() => import('@/pages/venues/financials/FinancialsPage'));
const VenueFloorPlanPage = lazy(() => import('@/pages/venues/floor-plan/VenueFloorPlanPage'));
const TipsPage = lazy(() => import('@/pages/venues/tips/TipsPage'));

// Team
const VenueTeamPage = lazy(() => import('@/pages/venues/VenueTeamPage'));

// Inventory
const InventoryPage = lazy(() => import('@/pages/venues/inventory/InventoryPage'));

// Accounting & VAT
const VenueAccountingPage = lazy(() => import('@/pages/venues/accounting/VenueAccountingPage'));

// CRM & Loyalty
const CrmPage = lazy(() => import('@/pages/venues/crm/CrmPage'));
const LoyaltyPage = lazy(() => import('@/pages/venues/crm/LoyaltyPage'));
const CampaignsPage = lazy(() => import('@/pages/venues/crm/CampaignsPage'));
const IdentityPage = lazy(() => import('@/pages/venues/crm/IdentityPage'));
const ZReportPage = lazy(() => import('@/pages/venues/accounting/ZReportPage'));

// Terminals & Card Payments
const TerminalsPage = lazy(() => import('@/pages/venues/terminals/TerminalsPage'));

// Reports & Analytics
const ReportsPage = lazy(() => import('@/pages/venues/reports/ReportsPage'));
const AnalyticsPage = lazy(() => import('@/pages/venues/analytics/AnalyticsPage'));


// Scale Tier (Phase 7)
const BookingsPage = lazy(() => import('@/pages/venues/bookings/BookingsPage'));
const WaitlistPage = lazy(() => import('@/pages/venues/waitlist/WaitlistPage'));
const PromotionsPage = lazy(() => import('@/pages/venues/promotions/PromotionsPage'));
const StoriesPage = lazy(() => import('@/pages/venues/stories/StoriesPage'));
const PortfolioPage = lazy(() => import('@/pages/organization/PortfolioPage'));
const ReviewsPage = lazy(() => import('@/pages/venues/reviews/ReviewsPage'));
const VenueNotificationsPage = lazy(() => import('@/pages/venues/notifications/VenueNotificationsPage'));

// Account
const AccountSettingsPage = lazy(() => import('@/pages/account/AccountSettingsPage'));

// Billing
const BillingPage = lazy(() => import('@/pages/billing/BillingPage'));

/**
 * HomeRedirect — decides where to send the user after login:
 *   - Has venues → first venue overview
 *   - No venues → /venues
 */
function HomeRedirect() {
  const { user, venues, loading, venuesLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading || venuesLoading) return;
    if (!user) return; // ProtectedRoute already handles redirect to login

    if (venues.length > 0) {
      void navigate(`/venues/${venues[0].slug}/overview`, { replace: true });
    } else {
      void navigate('/venues', { replace: true });
    }
  }, [user, venues, loading, venuesLoading, navigate]);

  return null;
}

function AppRoutes() {
  return (
    <ChunkErrorBoundary>
    <Suspense fallback={null}>
      <Routes>
        {/* ── Public auth routes ───────────────────────────── */}
        {/* Accept invite — public, no auth required */}
        <Route path="/auth/accept-invite" element={<AcceptInvitePage />} />

        <Route
          path="/auth/login"
          element={<PublicRoute><LoginPage /></PublicRoute>}
        />
        <Route
          path="/auth/register"
          element={<PublicRoute><RegisterPage /></PublicRoute>}
        />
        <Route
          path="/auth/forgot-password"
          element={<PublicRoute><ForgotPasswordPage /></PublicRoute>}
        />
        <Route
          path="/auth/reset-password"
          element={<ResetPasswordPage />}
        />
        <Route
          path="/auth/callback"
          element={<CallbackPage />}
        />
        <Route
          path="/integrations/ziina/callback"
          element={
            <ProtectedRoute skipOnboardingCheck>
              <ZiinaOAuthCallbackPage />
            </ProtectedRoute>
          }
        />

        {/* ── Onboarding (auth required, onboarding NOT required) ── */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute skipOnboardingCheck>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />

        {/* ── Protected app routes ─────────────────────────── */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          {/* Home → redirect to first venue */}
          <Route index element={<HomeRedirect />} />

          {/* Venue list */}
          <Route path="/venues" element={<VenuesPage />} />
          {/* Org-level, not venue-scoped: every venue on one screen. */}
          <Route path="/overview" element={<PortfolioPage />} />

          {/* Venue-scoped routes */}
          <Route path="/venues/:slug/overview" element={<VenueOverviewPage />} />
          <Route path="/venues/:slug/menu" element={<VenueMenuPage />} />
          <Route path="/venues/:slug/stories" element={<StoriesPage />} />
          <Route path="/venues/:slug/staff" element={<PlanGate minPlan="menu"><VenueStaffPage /></PlanGate>} />
          <Route path="/venues/:slug/venue-profile" element={<VenueSettingsPage />} />
          <Route path="/venues/:slug/integrations" element={<PlanGate minPlan="menu"><IntegrationsPage /></PlanGate>} />
          <Route path="/venues/:slug/integrations/ziina" element={<PlanGate minPlan="menu"><ZiinaIntegrationPage /></PlanGate>} />
          <Route path="/venues/:slug/qr-codes" element={<VenueQRPage />} />

          {/* ── Org-wide pages (not venue-scoped) ── */}
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/ai-credits" element={<PlanGate minPlan="menu"><VenueAICreditsPage /></PlanGate>} />
          {/* Backward compat: old venue-scoped AI credits route redirects to org-scoped */}
          <Route path="/venues/:slug/ai-credits" element={<Navigate to="/ai-credits" replace />} />

          {/* ── Menu tier (plan-gated) ── */}
          <Route path="/venues/:slug/inventory"    element={<PlanGate minPlan="scale"><InventoryPage /></PlanGate>} />
          <Route path="/venues/:slug/accounting"   element={<PlanGate minPlan="scale"><VenueAccountingPage /></PlanGate>} />
          <Route path="/venues/:slug/crm"          element={<PlanGate minPlan="scale"><CrmPage /></PlanGate>} />
          <Route path="/venues/:slug/loyalty"      element={<PlanGate minPlan="scale"><LoyaltyPage /></PlanGate>} />
          <Route path="/venues/:slug/campaigns"    element={<PlanGate minPlan="scale"><CampaignsPage /></PlanGate>} />
          <Route path="/venues/:slug/identity"     element={<PlanGate minPlan="scale"><IdentityPage /></PlanGate>} />
          <Route path="/venues/:slug/z-reports"    element={<PlanGate minPlan="scale"><ZReportPage /></PlanGate>} />
          <Route path="/venues/:slug/terminals"   element={<PlanGate minPlan="scale"><TerminalsPage /></PlanGate>} />
          <Route path="/venues/:slug/reports"    element={<PlanGate minPlan="menu"><ReportsPage /></PlanGate>} />
          <Route path="/venues/:slug/translations" element={<PlanGate minPlan="menu"><VenueTranslationsPage /></PlanGate>} />
          <Route path="/venues/:slug/analytics" element={<PlanGate minPlan="menu"><AnalyticsPage /></PlanGate>} />

          {/* ── Scale Tier (Phase 7) ── */}
          <Route path="/venues/:slug/bookings"     element={<PlanGate minPlan="scale"><BookingsPage /></PlanGate>} />
          <Route path="/venues/:slug/waitlist"     element={<PlanGate minPlan="scale"><WaitlistPage /></PlanGate>} />
          <Route path="/venues/:slug/promotions"   element={<PlanGate minPlan="scale"><PromotionsPage /></PlanGate>} />
          <Route path="/venues/:slug/orders"       element={<PlanGate minPlan="menu"><OrdersPage /></PlanGate>} />
          {/* The registry, not a literal: this gate and the guest app's `canReview` disagreed
              ('scale' here, 'operate' there), so an Operation venue collected reviews whose
              page redirected its owner to /billing. */}
          <Route path="/venues/:slug/reviews"      element={<PlanGate minPlan={FEATURE_REGISTRY.reviews.minPlan}><ReviewsPage /></PlanGate>} />
          <Route path="/venues/:slug/notifications" element={<PlanGate minPlan="scale"><VenueNotificationsPage /></PlanGate>} />
          <Route path="/venues/:slug/floor-plan"   element={<PlanGate minPlan="operate"><VenueFloorPlanPage /></PlanGate>} />
          <Route path="/venues/:slug/cashier"      element={<PlanGate minPlan="menu"><CashierPortalPage /></PlanGate>} />
          <Route path="/venues/:slug/tips"         element={<PlanGate minPlan="operate"><TipsPage /></PlanGate>} />
          <Route path="/venues/:slug/kitchen"      element={<PlanGate minPlan="menu"><KitchenPortalPage /></PlanGate>} />
          <Route path="/venues/:slug/bar"          element={<PlanGate minPlan="menu"><BarPortalPage /></PlanGate>} />
          <Route path="/venues/:slug/waiter"       element={<PlanGate minPlan="operate"><WaiterPortalPage /></PlanGate>} />
          <Route path="/venues/:slug/delivery"     element={<PlanGate minPlan="menu"><DeliveryPortalPage /></PlanGate>} />
          <Route path="/venues/:slug/delivery-hub" element={<PlanGate minPlan="scale"><DeliveryHubPage /></PlanGate>} />
          <Route path="/venues/:slug/financials"   element={<PlanGate minPlan="menu"><FinancialsPage /></PlanGate>} />
          <Route path="/venues/:slug/team"         element={<PlanGate minPlan="operate"><VenueTeamPage /></PlanGate>} />
          <Route
            path="/account/settings"
            element={<AccountSettingsPage />}
          />

        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
    </ChunkErrorBoundary>
  );
}

/**
 * Mirrors apps/guest/src/App.tsx — same values on purpose, so the two SPAs behave
 * alike and there's one convention to learn.
 *
 * `refetchOnWindowFocus: false` is the deliberate part. Refetch-on-focus is often
 * desirable in a dashboard, but this app has form pages that seed local state from
 * server data, and a background refetch landing mid-edit is how the Profile tab used
 * to eat what you were typing. Freshness here comes from explicit invalidation on
 * write (see src/api/queryKeys.ts), not from refetching behind the user's back.
 * Individual queries opt back in where it's actually wanted.
 *
 * Module scope, not inside the component: a client rebuilt on every render is a cache
 * that never caches.
 *
 * ⚠ SAFETY INVARIANT — every path that changes WHO is signed in must do a full page
 * load, not a client-side navigate. This client lives as long as the JS context, and
 * org-scoped keys (`billing.status`, `accounting.config`) carry no user/org
 * discriminator — so if account B were reached without a reload, B would be served A's
 * plan, seat counts and TRN. Today that can't happen: Topbar's sign-out, apiFetch's 401
 * handler and ImpersonationBanner's "end session" all call `window.location.replace`,
 * which destroys this cache. That is what makes the missing discriminator safe. If you
 * ever convert one of those to `navigate()`, you must first either key these by org or
 * `queryClient.clear()` on identity change. (Nothing is persisted to storage — there is
 * no persister — so the cache never outlives the tab.)
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <NotificationsProvider>
          <AuthProvider>
            <AppRoutes />
            <Toaster position="bottom-right" theme="system" />
            <CookieBanner />
          </AuthProvider>
        </NotificationsProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
